import { X } from 'lucide-react';
import { useId } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function Button({ variant = 'primary', className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  return <button className={`button button--${variant} ${className}`} {...props} />;
}

export function IconButton({ label, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button type="button" className={`icon-button ${className}`} aria-label={label} title={label} {...props} />;
}

export function Card({ children, className = '', ...props }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return <div className={`card ${className}`} {...props}>{children}</div>;
}

export function Field({ label, hint, className = '', ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return <label className={`field ${className}`}><span>{label}</span><input {...props} />{hint && <small>{hint}</small>}</label>;
}

export function SelectField({ label, children, className = '', ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return <label className={`field ${className}`}><span>{label}</span><select {...props}>{children}</select></label>;
}

export function TextAreaField({ label, className = '', ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string }) {
  return <label className={`field ${className}`}><span>{label}</span><textarea {...props} /></label>;
}

export function Modal({ open, onClose, title, children, footer, wide = false, className = '' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean; className?: string }) {
  const titleId = useId();
  if (!open) return null;
  return <div className={`modal-backdrop ${className ? `${className}__backdrop` : ''}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={`modal ${wide ? 'modal--wide' : ''} ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header><h2 id={titleId}>{title}</h2><IconButton label="Закрыть" onClick={onClose}><X size={22} /></IconButton></header>
      <div className="modal__body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </section>
  </div>;
}

export function StatusPill({ tone, children }: { tone: 'blue' | 'green' | 'yellow' | 'red' | 'violet' | 'gray'; children: ReactNode }) {
  return <span className={`status-pill status-pill--${tone}`}>{children}</span>;
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void }) {
  return <div className="segmented" role="tablist">{options.map((option) => <button key={option.value} className={option.value === value ? 'active' : ''} onClick={() => onChange(option.value)} role="tab" aria-selected={option.value === value}>{option.label}</button>)}</div>;
}

export function EmptyState({ icon, title, text, action }: { icon: ReactNode; title: string; text: string; action?: ReactNode }) {
  return <Card className="empty-state"><div className="empty-state__icon">{icon}</div><h3>{title}</h3><p>{text}</p>{action}</Card>;
}

export function RangeRow({ label, value, min = 0, max = 10, onChange }: { label: string; value?: number; min?: number; max?: number; onChange: (value: number) => void }) {
  return <label className="range-row"><span>{label}</span><input type="range" min={min} max={max} value={value ?? min} onChange={(event) => onChange(Number(event.target.value))} /><strong>{value ?? '—'}</strong></label>;
}
